// src/main.jsx
import React from 'react';
import ReactDOM from 'react-dom/client';

import { AppRouter } from './routes/AppRouter';
import { AuthProvider } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';

import './assets/styles/globals.css';
import './index.css';

// ==================== PROVIDERS ====================
const AppProviders = ({ children }) => (
  <AuthProvider>
    <AppProvider>
      {children}
    </AppProvider>
  </AuthProvider>
);

// ==================== ERROR BOUNDARY ====================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary capturou um erro:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full mx-auto text-center p-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops! Algo deu errado</h1>
            <p className="text-gray-600 mb-6">Ocorreu um erro inesperado. Nossa equipe foi notificada.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors"
            >
              Recarregar Página
            </button>
            {import.meta.env.DEV && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">Detalhes do erro (dev)</summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                  {this.state.error?.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== CONFIGURAÇÕES GLOBAIS ====================
document.documentElement.setAttribute('data-theme', 'light');

const applyUserTheme = (userType) => {
  const themes = {
    psicologo: 'theme-psicologo',
    supervisor: 'theme-supervisor',
    instituicao: 'theme-instituicao',
    internacional: 'theme-internacional'
  };
  Object.values(themes).forEach(theme => document.body.classList.remove(theme));
  if (themes[userType]) document.body.classList.add(themes[userType]);
};
window.applyUserTheme = applyUserTheme;

const updateMetaTags = (title, description) => {
  document.title = title ? `${title} | Psiando Global` : 'Psiando Global - Supervisão Clínica e Agenda Digital';
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && description) metaDescription.setAttribute('content', description);
};
window.updateMetaTags = updateMetaTags;

window.isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
window.smoothScrollTo = (element) => element?.scrollIntoView({ behavior: 'smooth', block: 'start' });

// ==================== LAZY LOADING ====================
if ('loading' in HTMLImageElement.prototype) {
  document.documentElement.classList.add('native-lazy-loading');
}

// ==================== SERVICE WORKER ====================
// SW desativado: sw.js não existe no projeto.
// Reativar apenas após criar sw.js real em /public

// ==================== ACESSIBILIDADE ====================
const setupAccessibility = () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.classList.add('reduce-motion');
  }
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    document.documentElement.classList.add('dark-mode-preferred');
  }
  const skipLink = document.createElement('a');
  skipLink.href = '#main-content';
  skipLink.textContent = 'Pular para o conteúdo principal';
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 bg-blue-600 text-white p-2 z-50';
  document.body.insertBefore(skipLink, document.body.firstChild);
};
setupAccessibility();

// ==================== AMBIENTE ====================
if (import.meta.env.PROD) {
  console.log = () => {};
  console.warn = () => {};
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
  });
}

if (import.meta.env.DEV) {
  window.__PSIANDO_DEBUG__ = {
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: import.meta.env.MODE,
    supabase: {
      url: import.meta.env.VITE_SUPABASE_URL,
      projectId: import.meta.env.VITE_SUPABASE_PROJECT_ID,
    },
    buildTime: new Date().toISOString()
  };
  console.log('🚀 Psiando Global - Supabase ativo');
  console.log('📱 Modo:', import.meta.env.MODE);
  console.log('🐛 Debug info disponível em window.__PSIANDO_DEBUG__');
}

// ==================== HMR CLEANUP ====================
if (import.meta.env.DEV && import.meta.hot) {
  import.meta.hot.dispose(() => {
    console.log('🔄 HMR: Cleanup realizado');
  });
}

// ==================== RENDERIZAÇÃO ====================
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Elemento root não encontrado! Verifique se existe <div id="root"></div> no index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppProviders>
        <AppRouter />
      </AppProviders>
    </ErrorBoundary>
  </React.StrictMode>
);

if (import.meta.env.DEV) {
  window.__PSIANDO_EXPORTS__ = { AppProviders, ErrorBoundary, applyUserTheme, updateMetaTags };
}